// ---------------------------------------------------------------------------
// Identificadores BCF estaveis para os topicos dos conflitos.
//
// O Guid do topico, o do viewpoint e o de cada comentario saem de um
// UUID versao 5 (RFC 9562: SHA-1 de namespace + nome), com um namespace
// proprio e um nome feito do identificador do projecto (o da sessao) e da
// chave do topico: o ID do grupo da proposta ("grupo:CHA139-001") ou a
// clashKey canonica. Voltar a exportar da os MESMOS Guids, e as outras
// plataformas (BIMcollab, Solibri, Revit, Navisworks) actualizam os
// topicos que ja tem (estado, comentario novo) em vez de os duplicar.
//
// SHA-1 sincrono aqui (e curto) para a montagem do BCF continuar pura e
// sincrona; nao serve para nada que precise de seguranca.
// ---------------------------------------------------------------------------

/** Namespace dos Guids dos conflitos VN2R (fixo: muda-lo duplicava os
 *  topicos ja enviados). */
export const NAMESPACE_CONFLITOS = '3f6b9a52-8c1e-4d7a-b2f0-6e5d4c3b2a19'

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s)
}

function rotl(x: number, n: number): number {
  return (x << n) | (x >>> (32 - n))
}

/** SHA-1 (FIPS 180-4) de bytes. */
export function sha1(bytes: Uint8Array): Uint8Array {
  const ml = bytes.length * 8
  const total = (((bytes.length + 8) >> 6) + 1) * 64
  const m = new Uint8Array(total)
  m.set(bytes)
  m[bytes.length] = 0x80
  const dv = new DataView(m.buffer)
  dv.setUint32(total - 8, Math.floor(ml / 2 ** 32))
  dv.setUint32(total - 4, ml >>> 0)
  let h0 = 0x67452301
  let h1 = 0xefcdab89
  let h2 = 0x98badcfe
  let h3 = 0x10325476
  let h4 = 0xc3d2e1f0
  const w = new Uint32Array(80)
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4)
    for (let i = 16; i < 80; i++) w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1)
    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4
    for (let i = 0; i < 80; i++) {
      let f: number
      let k: number
      if (i < 20) {
        f = (b & c) | (~b & d)
        k = 0x5a827999
      } else if (i < 40) {
        f = b ^ c ^ d
        k = 0x6ed9eba1
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d)
        k = 0x8f1bbcdc
      } else {
        f = b ^ c ^ d
        k = 0xca62c1d6
      }
      const t = (rotl(a, 5) + f + e + k + w[i]) >>> 0
      e = d
      d = c
      c = rotl(b, 30) >>> 0
      b = a
      a = t
    }
    h0 = (h0 + a) >>> 0
    h1 = (h1 + b) >>> 0
    h2 = (h2 + c) >>> 0
    h3 = (h3 + d) >>> 0
    h4 = (h4 + e) >>> 0
  }
  const out = new Uint8Array(20)
  const ov = new DataView(out.buffer)
  ;[h0, h1, h2, h3, h4].forEach((h, i) => ov.setUint32(i * 4, h))
  return out
}

function bytesDoUuid(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '')
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) throw new Error(`UUID inválido: ${uuid}`)
  const out = new Uint8Array(16)
  for (let i = 0; i < 16; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return out
}

/** UUID versao 5 (SHA-1) de um nome num namespace. */
export function uuidV5(nome: string, namespace: string = NAMESPACE_CONFLITOS): string {
  const ns = bytesDoUuid(namespace)
  const n = utf8(nome)
  const dados = new Uint8Array(ns.length + n.length)
  dados.set(ns)
  dados.set(n, ns.length)
  const h = sha1(dados).slice(0, 16)
  h[6] = (h[6] & 0x0f) | 0x50
  h[8] = (h[8] & 0x3f) | 0x80
  const hex = Array.from(h, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Guid do topico de um item (clashKey ou "grupo:<id>"). */
export function guidDoTopico(projecto: string, chave: string): string {
  return uuidV5(`${projecto}|topico|${chave}`)
}

/** Guid do viewpoint (unico por topico). */
export function guidDoViewpoint(projecto: string, chave: string): string {
  return uuidV5(`${projecto}|viewpoint|${chave}`)
}

/** Guid de um comentario: o mesmo evento da sempre o mesmo comentario. */
export function guidDoComentario(projecto: string, chave: string, data: string, texto: string): string {
  return uuidV5(`${projecto}|comentario|${chave}|${data}|${texto}`)
}

/** SHA-1 de um texto, em hexadecimal (identidade de grupos). */
export function hashHex(texto: string): string {
  return [...sha1(utf8(texto))].map((b) => b.toString(16).padStart(2, '0')).join('')
}
